import React, { useState, useEffect } from 'react';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { AdminUser } from '../../types/admin';
import { AdminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { sendInvitationEmail, generateFirebaseAuthLink } from '../../services/emailService';

const AdminInvitations: React.FC = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'super-admin' | 'admin'>('super-admin');
  const [pendingInvitations, setPendingInvitations] = useState<AdminUser[]>([]);
  const [activeAdmins, setActiveAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  // Get actionCodeSettings for admin invitation
  const getActionCodeSettings = (adminEmail: string) => ({
    url: `${window.location.origin}/admin?email=${encodeURIComponent(adminEmail)}`,
    handleCodeInApp: true,
  });

  const fetchData = async () => {
    try {
      const [pendingData, activeData] = await Promise.all([
        AdminService.getPendingInvitations(),
        AdminService.getActiveAdminUsers()
      ]);
      
      setPendingInvitations(pendingData);
      setActiveAdmins(activeData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      showToast('Failed to fetch admin data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInviteAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser?.uid) {
      showToast('Admin user not authenticated', 'error');
      return;
    }

    if (!email.trim()) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setSending(true);
    try {
      // 1. Create invitation record FIRST
      await AdminService.createAdminInvitation(
        email,
        role,
        currentUser.uid
      );

      // 2. Generate a real Firebase auth link via the server endpoint
      const adminUrl = import.meta.env.VITE_ADMIN_URL || window.location.origin;
      const continueUrl = `${adminUrl}/admin?email=${encodeURIComponent(email.toLowerCase())}`;

      const authLinkResult = await generateFirebaseAuthLink({
        email: email.toLowerCase(),
        continueUrl,
      });

      if (authLinkResult.success && authLinkResult.link) {
        // 3. Send the Firebase auth link via Resend
        const emailResult = await sendInvitationEmail({
          email: email.toLowerCase(),
          inviteLink: authLinkResult.link,
          type: 'admin',
          senderName:
            currentUser?.displayName ||
            currentUser?.email ||
            'A team member',
          senderEmail: currentUser?.email || undefined,
        });

        if (!emailResult.success) {
          // Resend failed — fall back to Firebase's own email delivery
          console.warn('Resend email failed, falling back to Firebase:', emailResult.error);
          const actionCodeSettings = getActionCodeSettings(email.toLowerCase());
          await sendSignInLinkToEmail(auth, email.toLowerCase(), actionCodeSettings);
        }
      } else {
        // Auth link generation failed — fall back to Firebase's own email delivery
        console.warn('Auth link generation failed, falling back to Firebase:', authLinkResult.error);
        const actionCodeSettings = getActionCodeSettings(email.toLowerCase());
        await sendSignInLinkToEmail(auth, email.toLowerCase(), actionCodeSettings);
      }
      
      showToast(`Admin invitation sent successfully to ${email}`, 'success');
      setEmail('');
      fetchData();
      
    } catch (error: any) {
      console.error('Error sending admin invitation:', error);
      showToast(`Failed to send invitation: ${error.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvitation = async (invitation: AdminUser) => {
    if (!currentUser?.uid) {
      showToast('Admin user not authenticated', 'error');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the invitation for ${invitation.email}?`
    );
    
    if (!confirmDelete) return;

    setDeletingId(invitation.id || null);
    try {
      await AdminService.deleteAdminInvitation(invitation.id || '');
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      showToast('Invitation deleted successfully', 'success');
    } catch (error: any) {
      console.error('Error deleting invitation:', error);
      showToast(`Failed to delete invitation: ${error.message}`, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRemoveAdmin = async (adminRecord: AdminUser) => {
    if (!currentUser?.uid) {
      showToast('Admin user not authenticated', 'error');
      return;
    }

    // Prevent removing the last super admin
    const superAdmins = activeAdmins.filter(user => user.role === 'super-admin');
    if (adminRecord.role === 'super-admin' && superAdmins.length === 1) {
      showToast('Cannot remove the last super admin', 'error');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${adminRecord.email} from admin list?`
    );
    
    if (!confirmDelete) return;

    try {
      await AdminService.removeAdminUser(adminRecord.id || '');
      setActiveAdmins(prev => prev.filter(admin => admin.id !== adminRecord.id));
      showToast('Admin removed successfully', 'success');
    } catch (error: any) {
      console.error('Error removing admin:', error);
      showToast(`Failed to remove admin: ${error.message}`, 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'px-2.5 py-1 text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full';
      case 'accepted':
      case 'active':
        return 'px-2.5 py-1 text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full';
      case 'expired':
        return 'px-2.5 py-1 text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full';
      default:
        return 'px-2.5 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full';
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent p-4 sm:p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          </div>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-48 bg-gray-200 dark:bg-gray-600 rounded-2xl animate-pulse" />
                  <div className="h-3 w-32 bg-gray-200 dark:bg-gray-600 rounded-2xl animate-pulse" />
                </div>
                <div className="h-6 w-20 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Current Active Admins */}
      <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Current Admins</h2>
        </div>

        <div className="space-y-3">
          {activeAdmins.map((adminRecord) => (
            <div key={adminRecord.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl gap-3">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {adminRecord.displayName || adminRecord.email}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{adminRecord.email}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {adminRecord.lastLogin ? `Last login: ${new Date(adminRecord.lastLogin).toLocaleDateString()}` : 'Never logged in'}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                  {adminRecord.role === 'super-admin' ? 'Full Access' : 'Limited Access'}
                </span>
                {/* Only hide delete for the last super-admin, allow deleting regular admins always */}
                {!(adminRecord.role === 'super-admin' && activeAdmins.filter(u => u.role === 'super-admin').length <= 1) && (
                  <button
                    onClick={() => handleRemoveAdmin(adminRecord)}
                    className="p-2 text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                    title="Remove admin"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite New Admin */}
      <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Invite New Admin</h2>
        <form onSubmit={handleInviteAdmin} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="sm:w-48">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'super-admin' | 'admin')}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="super-admin">Full Access</option>
                <option value="admin">Limited Access</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={sending}
              className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          Super Admins control billing and integrations. For student management, invite a Manager instead.
        </p>
      </div>

      {/* Pending Invitations */}
      <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent hover:shadow-sm dark:hover:shadow-purple-500/10 transition-all duration-200 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Pending Invitations</h3>
        {pendingInvitations.length > 0 ? (
          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{invitation.email}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={getStatusBadge(invitation.status || 'pending')}>
                      {(invitation.status || 'pending').charAt(0).toUpperCase() + (invitation.status || 'pending').slice(1)}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Role: {invitation.role === 'super-admin' ? 'Full Access' : 'Limited Access'}
                    </span>
                  </div>
                  {invitation.expiresAt && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {invitation.status === 'pending' && (
                  <button
                    onClick={() => handleDeleteInvitation(invitation)}
                    disabled={deletingId === invitation.id}
                    className="p-2 text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    title="Delete invitation"
                  >
                    {deletingId === invitation.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 dark:border-red-500"></div>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No pending invitations</p>
        )}
      </div>
    </div>
  );
};

export default AdminInvitations;